import React from "react";
import "./App.css";
import { useState, useEffect } from "react";
import {
  Box,
  FormField,
  Input,
  Dropdown,
  SegmentedToggle,
  Button,
  Page,
  Layout,
  Cell,
  TextButton,
  Heading,
  Text
} from "@wix/design-system";

export interface SlotBrand {
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  selectionColor: string;
  cornerRadius: number;
}

export interface DisabledOptions {
  invoiceTypes: string[];
  carrierTypes: string[];
}

const NO_DISABLED_OPTIONS: DisabledOptions = {
  invoiceTypes: [],
  carrierTypes: [],
};

const DEFAULT_SLOT_BRAND: SlotBrand = {
  backgroundColor: "#ffffff",
  textColor: "#151414",
  buttonColor: "#2f5dff",
  buttonTextColor: "#ffffff",
  selectionColor: "#2f5dff",
  cornerRadius: 8,
};

function parseSlotBrand(value: unknown): SlotBrand | null {
  if (!value) return null;
  try {
    const parsed = typeof value === "string" ? JSON.parse(decodeURIComponent(value)) : value;
    if (!parsed || typeof parsed !== "object") return null;
    const brand = parsed as Partial<SlotBrand>;
    if (typeof brand.backgroundColor !== "string" || typeof brand.textColor !== "string") return null;
    if (typeof brand.buttonColor !== "string" || typeof brand.buttonTextColor !== "string") return null;
    if (typeof brand.selectionColor !== "string" || typeof brand.cornerRadius !== "number") return null;
    return { ...DEFAULT_SLOT_BRAND, ...brand, cornerRadius: Math.max(0, brand.cornerRadius) };
  } catch {
    return null;
  }
}

function getInitialSlotBrand(): SlotBrand {
  const params = new URLSearchParams(window.location.search);
  return parseSlotBrand(params.get("slotBrand") || params.get("slot-brand")) || DEFAULT_SLOT_BRAND;
}

function parseDisabledOptions(value: unknown): DisabledOptions | null {
  if (!value || typeof value !== "object") return null;

  const props = value as Record<string, unknown>;
  const hasOptionProps =
    "disabledInvoiceTypes" in props || "disabledCarrierTypes" in props;
  if (!hasOptionProps) return null;

  return {
    invoiceTypes: Array.isArray(props.disabledInvoiceTypes)
      ? props.disabledInvoiceTypes.filter((option): option is string => typeof option === "string")
      : [],
    carrierTypes: Array.isArray(props.disabledCarrierTypes)
      ? props.disabledCarrierTypes.filter((option): option is string => typeof option === "string")
      : [],
  };
}

function getDisabledOptionsPayload(data: unknown): unknown {
  if (!data || typeof data !== "object") return undefined;

  const message = data as Record<string, unknown>;
  return message.props || message.payload || message;
}

// Translation object based on ry-woocommerce-ecpay-invoice-zh_TW.l10n.php
const translations: Record<"en" | "zh_TW", Record<string, string>> = {
  en: {
    "Invoice info": "Invoice info",
    "Invoice type": "Invoice type",
    Personal: "Personal",
    Company: "Company",
    Donate: "Donate",
    "Carrier type": "Carrier type",
    None: "None",
    "ECPay Member Carrier": "ECPay Member Carrier",
    "ezPay Host": "ezPay electronic invoice carrier (notify separately if winning)",
    MOICA: "MOICA",
    "Phone Barcode": "Phone Barcode",
    "Carrier Number (MOICA)": "Carrier Number (MOICA)",
    "Carrier Number (Phone Barcode)": "Carrier Number (Phone Barcode)",
    "Carrier Number": "Carrier Number",
    "Tax ID Number": "Tax ID Number",
    "Company Name": "Company Name",
    "Donate Number": "Donate Number",
    Submit: "Save",
    Skip: "Skip",
    English: "English",
    中文: "中文",
    Edit: "Edit",
    Saving: "Saving...",
  },
  zh_TW: {
    "Invoice info": "發票資訊",
    "Invoice type": "發票類型",
    Personal: "個人",
    Company: "公司",
    Donate: "捐贈",
    "Carrier type": "載具類型",
    None: "紙本發票",
    "ECPay Member Carrier": "綠界會員載具",
    "ezPay Host": "ezPay電子發票載具（中獎時另行通知）",
    MOICA: "自然人憑證",
    "Phone Barcode": getUrlParameter("providerId") === "ezpay" ? "手機條碼載具" : "手機條碼",
    "Carrier Number (MOICA)": "載具編號 (自然人憑證)",
    "Carrier Number (Phone Barcode)": "載具編號 (手機條碼)",
    "Carrier Number": "載具編號",
    "Tax ID Number": "統一編號",
    "Company Name": "公司名稱",
    "Donate Number": "捐贈愛心碼",
    Submit: "儲存",
    Skip: "略過",
    English: "English",
    中文: "中文",
    Edit: "編輯",
    Saving: "儲存中...",
  },
};

function App() {
  const [lang, setLang] = useState<"en" | "zh_TW">("zh_TW");
  const [disabled, setDisabled] = useState(false);
  const [slotBrand, setSlotBrand] = useState<SlotBrand>(getInitialSlotBrand);
  const [disabledOptions, setDisabledOptions] = useState<DisabledOptions>(NO_DISABLED_OPTIONS);
  const embedded = !isStandaloneMode();
  const t: (key: string) => string = (key) => translations[lang][key] || key;

  useEffect(() => {
    const root = document.body;
    const values: Record<string, string> = {
      "--invoice-slot-background": slotBrand.backgroundColor,
      "--invoice-slot-text": slotBrand.textColor,
      "--invoice-slot-button": slotBrand.buttonColor,
      "--invoice-slot-button-text": slotBrand.buttonTextColor,
      "--invoice-slot-selection": slotBrand.selectionColor,
      "--invoice-slot-radius": `${slotBrand.cornerRadius}px`,
      "--wds-color-fill-surface-standard": slotBrand.backgroundColor,
      "--wds-color-fill-surface-raised": slotBrand.backgroundColor,
      "--wds-color-fill-standard-primary": slotBrand.backgroundColor,
      "--wds-color-fill-standard-secondary": slotBrand.backgroundColor,
      "--wds-color-fill-standard-tertiary": slotBrand.backgroundColor,
      "--wds-color-fill-standard-tertiary-hover": slotBrand.selectionColor,
      "--wds-color-fill-standard-tertiary-active": slotBrand.selectionColor,
      "--wds-color-fill-surface-default": slotBrand.backgroundColor,
      "--wds-color-fill-surface-overlay": slotBrand.backgroundColor,
      "--wds-popover-fill": slotBrand.backgroundColor,
      "--wds-list-item-select-fill": slotBrand.backgroundColor,
      "--wds-list-item-select-fill-hover": slotBrand.selectionColor,
      "--wds-list-item-select-fill-active": slotBrand.selectionColor,
      "--wds-list-item-select-active-fill": slotBrand.selectionColor,
      "--wds-list-item-select-active-fill-hover": slotBrand.selectionColor,
      "--wds-list-item-select-title": slotBrand.textColor,
      "--wds-list-item-select-title-disabled": slotBrand.textColor,
      "--wds-list-item-select-active-title": slotBrand.buttonTextColor,
      "--wds-list-item-select-padding-vertical-medium": "6px",
      "--wds-list-item-select-padding-horizontal-medium": "12px",
      "--wds-font-size-body-medium": "14px",
      "--wds-font-line-height-body-medium": "20px",
      "--wds-input-value-font-size-medium": "14px",
      "--wds-input-value-font-line-height-medium": "20px",
      "--wds-scrollbar-fill": slotBrand.selectionColor,
      "--wds-scrollbar-fill-hover": slotBrand.selectionColor,
      "--wds-scrollbar-fill-active": slotBrand.selectionColor,
      "--wds-border-radius-surface-overlay": `${slotBrand.cornerRadius}px`,
      "--wds-color-white": slotBrand.backgroundColor,
      "--wds-color-fill-dark-primary": slotBrand.buttonColor,
      "--wds-color-fill-dark-secondary": slotBrand.buttonColor,
      "--wds-color-fill-dark-tertiary": slotBrand.backgroundColor,
      "--wds-color-text-standard-primary": slotBrand.textColor,
      "--wds-color-text-standard-secondary": slotBrand.textColor,
      "--wds-color-text-primary": slotBrand.textColor,
      "--wds-color-text-placeholder": slotBrand.textColor,
      "--wds-form-field-label-fill": slotBrand.textColor,
      "--wds-divider-fill-light": slotBrand.textColor,
      "--wds-divider-fill-dark": slotBrand.textColor,
      "--wds-dropdown-icon-fill": slotBrand.textColor,
      "--wds-input-background-fill": slotBrand.backgroundColor,
      "--wds-input-background-fill-default": slotBrand.backgroundColor,
      "--wds-input-background-fill-hover": slotBrand.backgroundColor,
      "--wds-input-border-color": slotBrand.textColor,
      "--wds-input-border-color-hover": slotBrand.textColor,
      "--wds-input-border-color-focus": slotBrand.buttonColor,
      "--wds-input-border-radius-default-small": `${slotBrand.cornerRadius}px`,
      "--wds-input-border-radius-default-medium": `${slotBrand.cornerRadius}px`,
      "--wds-popover-menu-border-radius": `${slotBrand.cornerRadius}px`,
      "--wds-button-fill-dark-secondary": slotBrand.buttonColor,
      "--wds-button-fill-dark-secondary-hover": slotBrand.buttonColor,
      "--wds-button-fill-dark-secondary-active": slotBrand.buttonColor,
      "--wds-button-label-fill-dark-secondary": slotBrand.buttonTextColor,
      "--wds-button-border-radius-small": `${slotBrand.cornerRadius}px`,
    };
    Object.entries(values).forEach(([name, value]) => root.style.setProperty(name, value));
  }, [slotBrand]);

  useEffect(() => {
    // Only postMessage if not in standalone mode
    if (!isStandaloneMode()) {
      // Post READY message to parent
      window.parent.postMessage({
        eventName: "READY"
      }, "*");
    }

    // Checkout may push language and brand updates repeatedly during live preview.
    const handleMessage = (event: MessageEvent) => {
      console.debug("[InvoiceFields] postMessage received", {
        origin: event.origin,
        data: event.data,
      });

      if (event.data?.currentLanguage) {
        const newLang = event.data.currentLanguage === "zh" ? "zh_TW" : "en";
        setLang(newLang);
        console.debug("[InvoiceFields] language updated", newLang);
      }
      const nextBrand = parseSlotBrand(
        event.data?.slotBrand || event.data?.["slot-brand"] || event.data?.props?.slotBrand
      );
      if (nextBrand) setSlotBrand(nextBrand);
      if (event.data?.slotBrand || event.data?.["slot-brand"] || event.data?.props?.slotBrand) {
        console.debug("[InvoiceFields] slotBrand parsed", Boolean(nextBrand));
      }

      const disabledOptionsPayload = getDisabledOptionsPayload(event.data);
      console.debug("[InvoiceFields] option props received", disabledOptionsPayload);
      const nextDisabledOptions = parseDisabledOptions(disabledOptionsPayload);
      if (nextDisabledOptions) {
        setDisabledOptions(nextDisabledOptions);
        console.debug("[InvoiceFields] disabled options parsed", nextDisabledOptions);
      } else {
        console.debug(
          "[InvoiceFields] no disabled option props found; expected props.disabledInvoiceTypes or props.disabledCarrierTypes",
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []); // Empty dependency array means this runs once on mount

  // Header with checkmark, title, and Edit button
  function Header() {
    return (
      <Box direction="horizontal" align="center" verticalAlign="middle" padding="12px 0">
        {/* {disabled && <Confirm/>} */}
        <Heading size="large">{t("Invoice info")}</Heading>
        <Box flexGrow={1} />
        {disabled && (
          <TextButton size="medium" skin="dark" onClick={() => setDisabled(false)} underline="always">
            {t("Edit")}
          </TextButton>
        )}
      </Box>
    );
  }

  const brandStyle = {
    "--invoice-slot-background": slotBrand.backgroundColor,
    "--invoice-slot-text": slotBrand.textColor,
    "--invoice-slot-button": slotBrand.buttonColor,
    "--invoice-slot-button-text": slotBrand.buttonTextColor,
    "--invoice-slot-selection": slotBrand.selectionColor,
    "--invoice-slot-radius": `${slotBrand.cornerRadius}px`,
  } as React.CSSProperties;

  return (
    <div className={`invoice-slot-shell${embedded ? " invoice-slot-embedded" : ""}`} style={brandStyle}>
      <div className={`invoice-slot-content${embedded ? " invoice-slot-embedded" : ""}`}>
      <Page minWidth={0} height="100vh">
      <Page.Content
        fullScreen={
          // check if embed is true
          window.parent !== window
        }
      >
        <Layout>
          {window.parent === window && <Cell span={12}>
            <Box align="right">
              <Box width="120px">
                <Dropdown
                  options={[
                    { id: "en", value: t("English") },
                    { id: "zh_TW", value: t("中文") },
                  ]}
                  selectedId={lang}
                  onSelect={({ id }) => setLang(id as "en" | "zh_TW")}
                  placeholder={t("English")}
                  disabled={disabled}
                  ariaLabel={t("English")}
                />
              </Box>
            </Box>
          </Cell>}
          <Cell span={12}>
            <Header />
          </Cell>
          <Cell span={12}>
            <InvoiceFieldsForm
              lang={lang}
              t={t}
              disabled={disabled}
              setDisabled={setDisabled}
              isStandalone={isStandaloneMode()}
              disabledOptions={disabledOptions}
            />
          </Cell>
        </Layout>
      </Page.Content>
      </Page>
      </div>
    </div>
  );
}

// Add helper function to get URL parameters
function getUrlParameter(name: string): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

function getStandaloneContext() {
  return {
    checkoutId: getUrlParameter("checkoutId"),
    // Payment-provider redirects use `t`; support explicit names as well so a
    // standalone invoice page can be linked directly by other callers.
    transactionId:
      getUrlParameter("pluginTransactionId") ||
      getUrlParameter("transactionId") ||
      getUrlParameter("t"),
    token: getUrlParameter("token"),
  };
}

// Redirects identify the payment by transaction ID, while checkout pages use
// checkoutId. Either identifier means this form owns its own submission.
function isStandaloneMode(): boolean {
  const { checkoutId, transactionId } = getStandaloneContext();
  return checkoutId !== null || transactionId !== null;
}

function reportDropdownHeight(dropdownOpen: boolean) {
  if (isStandaloneMode()) return;
  window.setTimeout(() => {
    const optionBottoms = Array.from(document.querySelectorAll('[role="option"]'))
      .map((option) => option.getBoundingClientRect().bottom + window.scrollY);
    const contentHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      ...optionBottoms.map((bottom) => Math.ceil(bottom + 16)),
    );
    window.parent.postMessage({
      eventName: "SET_HEIGHT",
      payload: { height: contentHeight, dropdownOpen },
    }, "*");
  }, 0);
}

const invoiceTypeOptions = [
  { value: "personal", key: "Personal" },
  { value: "company", key: "Company" },
  { value: "donate", key: "Donate" },
];

// Move carrier type options into a function to filter based on provider
function getCarrierTypeOptions(providerId: string | null) {
  const allOptions = [
    { value: "none", key: "None" },
    { value: "ecpay_host", key: providerId === "ezpay" ? "ezPay Host" : "ECPay Member Carrier" },
    { value: "MOICA", key: "MOICA" },
    { value: "phone_barcode", key: "Phone Barcode" },
  ];

  if (providerId === "ezpay") {
    return allOptions.filter(opt => 
      !["none", "MOICA"].includes(opt.value)
    );
  }

  return allOptions;
}

// Helper for status prop
const getStatus = (error: any) => (error ? "error" : undefined);

function InvoiceFieldsForm({
  lang,
  t,
  disabled,
  setDisabled,
  isStandalone,
  disabledOptions,
}: {
  lang: "en" | "zh_TW";
  t: (key: string) => string;
  disabled: boolean;
  setDisabled: (b: boolean) => void;
  isStandalone: boolean;
  disabledOptions: DisabledOptions;
}) {
  const [invoiceType, setInvoiceType] = useState("personal");
  const [carrierType, setCarrierType] = useState("ecpay_host");
  const [fields, setFields] = useState({
    invoice_carrier_no: "",
    invoice_no: "",
    invoice_donate_no: "",
    invoice_company_name: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset carrier type if current selection is not valid for ezpay
  useEffect(() => {
    const providerId = getUrlParameter("providerId");
    if (providerId === "ezpay" && ["none", "MOICA"].includes(carrierType)) {
      setCarrierType("ecpay_host");
    }
  }, [carrierType]);

  useEffect(() => {
    const availableInvoiceTypes = invoiceTypeOptions.filter(
      (option) => !disabledOptions.invoiceTypes.includes(option.value),
    );
    if (
      disabledOptions.invoiceTypes.includes(invoiceType) &&
      availableInvoiceTypes.length > 0
    ) {
      setInvoiceType(availableInvoiceTypes[0].value);
    }

    const availableCarrierTypes = getCarrierTypeOptions(getUrlParameter("providerId"))
      .filter((option) => !disabledOptions.carrierTypes.includes(option.value));
    if (
      disabledOptions.carrierTypes.includes(carrierType) &&
      availableCarrierTypes.length > 0
    ) {
      setCarrierType(availableCarrierTypes[0].value);
    }
  }, [disabledOptions, invoiceType, carrierType]);

  // Show/hide and required logic
  const showCarrierNo =
    invoiceType === "personal" &&
    ["MOICA", "phone_barcode"].includes(carrierType);
  const showTaxId = invoiceType === "company";
  const showCompanyName = invoiceType === "company";
  const showDonateNo = invoiceType === "donate";

  // Validation patterns
  const patterns = {
    invoice_carrier_no: {
      MOICA: /^[A-Z]{2}\d{14}$/,
      phone_barcode: /^\/[0-9A-Z+-.]{7}$/,
    },
    invoice_no: /^[0-9]{8}$/,
    invoice_donate_no: /^[0-9]{3,7}$/,
  };

  // Wix controls expect options as {id, value}. Compact option sets use a
  // SegmentedToggle; larger sets stay as a Dropdown.
  const wixInvoiceTypeOptions = invoiceTypeOptions
    .filter((opt) => !disabledOptions.invoiceTypes.includes(opt.value))
    .map((opt) => ({
      id: opt.value,
      value: t(opt.key),
    }));
  const wixCarrierTypeOptions = getCarrierTypeOptions(getUrlParameter("providerId"))
    .filter((opt) => !disabledOptions.carrierTypes.includes(opt.value))
    .map((opt) => ({
      id: opt.value,
      value: t(opt.key),
    }));
  const invoiceTypeUsesSegmentedToggle = wixInvoiceTypeOptions.length <= 3;
  const carrierTypeUsesSegmentedToggle = wixCarrierTypeOptions.length <= 4;

  useEffect(() => {
    console.debug("[InvoiceFields] hidden type options", {
      invoiceTypes: disabledOptions.invoiceTypes,
      carrierTypes: disabledOptions.carrierTypes,
      renderedInvoiceTypes: wixInvoiceTypeOptions.map((option) => option.id),
      renderedCarrierTypes: wixCarrierTypeOptions.map((option) => option.id),
    });
  }, [disabledOptions]);

  function handleDropdownChange(name: string, value: string) {
    if (name === "invoice_type") {
      setInvoiceType(value);
      if (value !== "personal") {
        setCarrierType("ecpay_host");
      }
    } else if (name === "invoice_carrier_type") {
      setCarrierType(value);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors: any = {};
    // Required field validation
    if (invoiceType === "personal") {
      if (showCarrierNo) {
        if (!fields.invoice_carrier_no) {
          newErrors.invoice_carrier_no =
            lang === "zh_TW"
              ? carrierType === "MOICA"
                ? "請輸入載具編號 (自然人憑證)"
                : "請輸入載具編號 (手機條碼)"
              : carrierType === "MOICA"
              ? "Please enter MOICA number"
              : "Please enter phone barcode";
        } else if (
          carrierType === "MOICA" &&
          !patterns.invoice_carrier_no.MOICA.test(fields.invoice_carrier_no)
        ) {
          newErrors.invoice_carrier_no =
            lang === "zh_TW"
              ? "無效的載具編號 (自然人憑證格式)"
              : "Invalid MOICA number (should be 2 letters + 14 digits)";
        } else if (
          carrierType === "phone_barcode" &&
          !patterns.invoice_carrier_no.phone_barcode.test(fields.invoice_carrier_no)
        ) {
          newErrors.invoice_carrier_no =
            lang === "zh_TW"
              ? "無效的載具編號 (手機條碼格式)"
              : "Invalid phone barcode (should start with / and be 8 chars)";
        }
      }
    } else if (invoiceType === "company") {
      if (!fields.invoice_no) {
        newErrors.invoice_no =
          lang === "zh_TW" ? "請輸入統一編號" : "Please enter tax ID number";
      } else if (!patterns.invoice_no.test(fields.invoice_no)) {
        newErrors.invoice_no =
          lang === "zh_TW"
            ? "無效的統一編號 (8碼數字)"
            : "Invalid tax ID number (should be 8 digits)";
      }
      if (!fields.invoice_company_name) {
        newErrors.invoice_company_name =
          lang === "zh_TW" ? "公司名稱為必填" : "Company name is required";
      }
    } else if (invoiceType === "donate") {
      if (!fields.invoice_donate_no) {
        newErrors.invoice_donate_no =
          lang === "zh_TW" ? "請輸入愛心碼" : "Please enter donate number";
      } else if (!patterns.invoice_donate_no.test(fields.invoice_donate_no)) {
        newErrors.invoice_donate_no =
          lang === "zh_TW"
            ? "無效的愛心碼 (3-7碼數字)"
            : "Invalid donate number (should be 3-7 digits)";
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      const providerId = getUrlParameter("providerId");
      // Map user input to API field names and log
      const apiData = providerId === "ezpay" ? {
        // Category: B2B=買受人為營業人, B2C=買受人為個人
        Category: invoiceType === "company" ? "B2B" : "B2C",
        // CarrierType: 0=手機條碼載具, 1=自然人憑證條碼載具, 2=ezPay電子發票載具
        CarrierType: 
          invoiceType === "personal"
            ? carrierType === "phone_barcode"
              ? "0"
              : carrierType === "MOICA"
              ? "1"
              : carrierType === "ecpay_host"
              ? "2"
              : ""
            : "",
        // CarrierNum: 載具編號
        CarrierNum:
          invoiceType === "personal"
            ? carrierType === "MOICA" || carrierType === "phone_barcode"
              ? fields.invoice_carrier_no
              : carrierType === "ecpay_host"
              ? fields.invoice_carrier_no || "default" // Use a default value or user identifier for ezPay host
              : ""
            : "",
        // LoveCode: 捐贈碼
        LoveCode: invoiceType === "donate" ? fields.invoice_donate_no : "",
        // Add company information for ezpay
        BuyerName: invoiceType === "company" ? fields.invoice_company_name : undefined,
        BuyerUBN: invoiceType === "company" ? fields.invoice_no : undefined,
        // PrintFlag: Y=索取, N=不索取
        PrintFlag: 
          invoiceType === "company" || 
          (invoiceType === "personal" && !carrierType && !fields.invoice_donate_no) 
            ? "Y" 
            : "N",
      } : {
        // Original ECPay format
        CustomerIdentifier: invoiceType === "company" ? fields.invoice_no : undefined,
        CustomerName: invoiceType === "company" ? fields.invoice_company_name : undefined,
        Donation: invoiceType === "donate" ? "1" : "0",
        LoveCode: invoiceType === "donate" ? fields.invoice_donate_no : undefined,
        // Print: 0=store electronically, 1=request paper invoice.
        Print:
          invoiceType === "donate"
            ? "0"
            : carrierType === "none"
            ? "1"
            : "0",
        CarrierType:
          invoiceType === "personal"
            ? carrierType === "MOICA"
              ? "2"
              : carrierType === "phone_barcode"
              ? "3"
              : carrierType === "ecpay_host"
              ? "1"
              : ""
            : "",
        CarrierNum:
          invoiceType === "personal"
            ? carrierType === "MOICA"
              ? fields.invoice_carrier_no
              : carrierType === "phone_barcode"
              ? fields.invoice_carrier_no
              : ""
            : "",
      };
      // Remove undefined fields
      Object.keys(apiData).forEach(
        (k) => (apiData as Record<string, unknown>)[k] === undefined && delete (apiData as Record<string, unknown>)[k]
      );
      console.log(apiData);
      
      if (isStandalone) {
        // Standalone mode: POST to endpoint and redirect
        handleStandaloneSubmit(apiData);
      } else {
        // Iframe mode: postMessage to parent
        window.parent.postMessage({
          action: "submit_invoice_fields",
          data: apiData,
        }, "*");
        setDisabled(true); // Disable all fields after submit
      }
    }
  }

  async function handleStandaloneSubmit(apiData: any) {
    const { checkoutId, transactionId, token } = getStandaloneContext();
    const metadataId = transactionId || checkoutId;
    
    if (!metadataId) {
      console.error("A checkout or transaction ID is required in standalone mode");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `https://pay-api.certifiedcode.us/_functions/apps/06018aa1-bcbe-47fa-88e2-dd737ffa0c9d/checkout-metadata/${encodeURIComponent(metadataId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token || "",
          },
          body: JSON.stringify({
            invoice: apiData,
            checkoutId: checkoutId || undefined,
            pluginTransactionId: transactionId || undefined,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Redirect to the redirectUrl from response
      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      } else {
        console.error("No redirectUrl in response");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error submitting invoice fields:", error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  }

  async function handleSkip() {
    const { checkoutId, transactionId, token } = getStandaloneContext();
    const metadataId = transactionId || checkoutId;
    
    if (!metadataId) {
      console.error("A checkout or transaction ID is required in standalone mode");
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit empty invoice data to skip
      const response = await fetch(
        `https://pay-api.certifiedcode.us/_functions/apps/06018aa1-bcbe-47fa-88e2-dd737ffa0c9d/checkout-metadata/${encodeURIComponent(metadataId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token || "",
          },
          body: JSON.stringify({
            invoice: {},
            checkoutId: checkoutId || undefined,
            pluginTransactionId: transactionId || undefined,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Redirect to the redirectUrl from response
      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      } else {
        console.error("No redirectUrl in response");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error skipping invoice fields:", error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box direction="vertical" gap="12px" paddingBottom={!disabled ? "12px" : undefined}>
        <FormField
          label={t("Invoice type")}
          labelSize="medium"
          required
          status={getStatus(undefined)}
        >
          {invoiceTypeUsesSegmentedToggle ? (
            <SegmentedToggle
              selected={invoiceType}
              size="small"
              onClick={(_, value) => handleDropdownChange("invoice_type", value)}
              disabled={disabled || isSubmitting}
              // fullWidth
              ariaLabel={t("Invoice type")}
            >
              {wixInvoiceTypeOptions.map((option) => (
                <SegmentedToggle.Button
                  key={option.id}
                  value={option.id}
                  selected={option.id === invoiceType}
                >
                  {option.value}
                </SegmentedToggle.Button>
              ))}
            </SegmentedToggle>
          ) : (
            <Dropdown
              options={wixInvoiceTypeOptions}
              selectedId={invoiceType}
              onSelect={({ id }) =>
                handleDropdownChange("invoice_type", id as string)
              }
              onOptionsShow={() => reportDropdownHeight(true)}
              onOptionsHide={() => reportDropdownHeight(false)}
              placeholder={t("Invoice type")}
              readOnly={disabled || isSubmitting}
              ariaLabel={t("Invoice type")}
            />
          )}
        </FormField>
        {invoiceType === "personal" && (
          <FormField
            label={t("Carrier type")}
            labelSize="medium"
            required
            status={getStatus(undefined)}
          >
            {carrierTypeUsesSegmentedToggle ? (
              <SegmentedToggle
                selected={carrierType}
                size="small"
                onClick={(_, value) =>
                  handleDropdownChange("invoice_carrier_type", value)
                }
                disabled={disabled || isSubmitting}
                // fullWidth
                ariaLabel={t("Carrier type")}
              >
                {wixCarrierTypeOptions.map((option) => (
                  <SegmentedToggle.Button
                    key={option.id}
                    value={option.id}
                    selected={option.id === carrierType}
                    aria-label={option.value}
                    title={option.value}
                    style={{ flexBasis: "auto" }}
                  >
                    <Text size="tiny" weight="normal" skin="primary">
                      {option.value}
                    </Text>
                  </SegmentedToggle.Button>
                ))}
              </SegmentedToggle>
            ) : (
              <Dropdown
                options={wixCarrierTypeOptions}
                selectedId={carrierType}
                onSelect={({ id }) =>
                  handleDropdownChange("invoice_carrier_type", id as string)
                }
                onOptionsShow={() => reportDropdownHeight(true)}
                onOptionsHide={() => reportDropdownHeight(false)}
                placeholder={t("Carrier type")}
                readOnly={disabled || isSubmitting}
                ariaLabel={t("Carrier type")}
              />
            )}
          </FormField>
        )}
        {showCarrierNo && (
          <FormField
            label={
              carrierType === "MOICA"
                ? t("Carrier Number (MOICA)")
                : t("Carrier Number (Phone Barcode)")
            }
            labelSize="medium"
            required
            status={getStatus(errors.invoice_carrier_no)}
            statusMessage={errors.invoice_carrier_no}
          >
            <Input
              name="invoice_carrier_no"
              value={fields.invoice_carrier_no}
              onChange={handleInputChange}
              placeholder={
                carrierType === "MOICA" ? "AB12345678901234" : "/XXXXXXX"
              }
              readOnly={disabled || isSubmitting}
              ariaLabel={carrierType === "MOICA"
                ? t("Carrier Number (MOICA)")
                : t("Carrier Number (Phone Barcode)")}
            />
          </FormField>
        )}
        {showTaxId && (
          <FormField
            label={t("Tax ID Number")}
            labelSize="medium"
            required
            status={getStatus(errors.invoice_no)}
            statusMessage={errors.invoice_no}
          >
            <Input
              name="invoice_no"
              value={fields.invoice_no}
              onChange={handleInputChange}
              placeholder="12345678"
              readOnly={disabled || isSubmitting}
              ariaLabel={t("Tax ID Number")}
            />
          </FormField>
        )}
        {showCompanyName && (
          <FormField
            label={t("Company Name")}
            labelSize="medium"
            required
            status={getStatus(errors.invoice_company_name)}
            statusMessage={errors.invoice_company_name}
          >
            <Input
              name="invoice_company_name"
              value={fields.invoice_company_name}
              onChange={handleInputChange}
              readOnly={disabled || isSubmitting}
              ariaLabel={t("Company Name")}
            />
          </FormField>
        )}
        {showDonateNo && (
          <FormField
            label={t("Donate Number")}
            labelSize="medium"
            required
            status={getStatus(errors.invoice_donate_no)}
            statusMessage={errors.invoice_donate_no}
          >
            <Input
              name="invoice_donate_no"
              value={fields.invoice_donate_no}
              onChange={handleInputChange}
              placeholder={getUrlParameter("providerId") === "ezpay" ? "" : "7261651"}
              readOnly={disabled || isSubmitting}
              ariaLabel={t("Donate Number")}
            />
          </FormField>
        )}
        {!disabled && (
          <>
            <Box marginTop="12px">
              <Button className="invoice-primary-action" type="submit" fullWidth disabled={isSubmitting}>
                {isSubmitting ? t("Saving") : t("Submit")}
              </Button>
            </Box>
            {isStandalone && (
              <Box marginTop="12px">
                <Button skin="light" fullWidth onClick={handleSkip} disabled={isSubmitting}>
                  {isSubmitting ? t("Saving") : t("Skip")}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </form>
  );
}

export default App;
